import useRouterState from '@discover-use-router-state';
import { buildCuisinePath } from '@/lib/cityCuisine';
import useCitySlug from '../useCitySlug';
import getValidTagIds from './getValid';
import getTagOptions from './getOptions';

const isString = (value: unknown): value is string => typeof value === 'string';

export default function useTagIdsQueryState(
  tags: {
    category: string | null;
    title: string | null;
    id: number | null;
  }[]
): [number[], (newValues: number[] | null) => Promise<void>] {
  const { optionKeys, optionsByKey } = getTagOptions(tags);
  const [{ cuisine, locationPath, cuisineRoutes }] = useCitySlug();

  const [routerValues, setRouterValues] = useRouterState([
    ...new Set([...optionKeys, 'Cuisine', 'slug']),
  ]);
  // A cuisine page carries its cuisine in the path, which wins over a conflicting Cuisine query.
  const values = cuisine ? { ...routerValues, Cuisine: [String(cuisine.id)] } : routerValues;

  const validRouterValues = getValidTagIds(values, optionKeys, optionsByKey);

  return [
    validRouterValues,
    async (newValues: number[] | null) => {
      const newRouterValues = Object.fromEntries(
        Object.entries(optionsByKey).map(([key, ids]) => {
          const newRouterIds = ids.filter((id) => newValues?.includes(id)).map((id) => `${id}`);

          return [key, newRouterIds.length === 0 ? undefined : newRouterIds];
        })
      );
      // Instances that do not own the Cuisine category keep the current cuisine selection.
      const cuisineIds =
        'Cuisine' in optionsByKey
          ? newRouterValues.Cuisine || []
          : (values.Cuisine || []).filter(isString);
      const { pathname, cuisineIds: queryCuisineIds } = buildCuisinePath(
        locationPath,
        cuisineIds,
        cuisineRoutes
      );

      await setRouterValues(
        { ...newRouterValues, Cuisine: queryCuisineIds, slug: [] },
        { pathname }
      );
    },
  ];
}
